import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import { toast } from 'sonner';
import { RefreshCw, Sparkles, PlusCircle, ArrowUpRight, Wallet, PieChart as PieIcon } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Button } from '@/components/ui/button';

const ASSET_COLORS = {
  stocks: '#0F172A',
  bonds: '#4F46E5',
  crypto: '#F59E0B',
  cash: '#10B981',
  real_estate: '#EC4899',
  other: '#64748B',
};

const fmt = (n, currency = 'USD') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n || 0);

const WealthOverview = () => {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyRange, setHistoryRange] = useState('3m');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [savingsRate, setSavingsRate] = useState(null); // {current_pct, target_pct}

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wealth/summary`);
      setSummary(res.data);
    } catch (e) {
      toast.error('Failed to load wealth summary');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (range) => {
    try {
      const res = await axios.get(`${API}/wealth/networth/history?range=${range}`);
      setHistory(res.data.series || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const fetchSavingsRate = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/wealth/savings-target`),
        axios.get(`${API}/analytics/summary`),
      ]);
      const income = s.data?.summary?.total_income || 0;
      const exp = s.data?.summary?.total_expenses || 0;
      const current = income > 0 ? Math.max(0, Math.min(100, ((income - exp) / income) * 100)) : 0;
      setSavingsRate({ current_pct: current, target_pct: t.data?.target_pct ?? 20 });
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchSavingsRate();
  }, [fetchSummary, fetchSavingsRate]);

  useEffect(() => {
    fetchHistory(historyRange);
  }, [fetchHistory, historyRange]);

  const handleRefreshPrices = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/wealth/prices/refresh`);
      const { updated, failed, live_source } = res.data;
      if (!live_source && failed.length === 0) {
        toast.info('No refreshable holdings yet.');
      } else if (updated > 0) {
        toast.success(`Refreshed ${updated} price${updated === 1 ? '' : 's'}` + (failed.length ? ` · ${failed.length} failed` : ''));
      } else if (failed.length) {
        toast.warning(`No prices updated. ${failed.length} failed — add ALPHAVANTAGE_API_KEY for stocks.`);
      }
      await fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Price refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleGetInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await axios.post(`${API}/wealth/ai/insights`);
      setInsights(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI insights failed');
    } finally {
      setLoadingInsights(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 md:p-12" data-testid="wealth-loading">
        <div className="text-slate-900 font-chivo text-xl">Loading your portfolio…</div>
      </div>
    );
  }

  const pieData = (summary?.allocation || [])
    .filter((a) => a.value > 0)
    .map((a) => ({ name: a.asset_class, value: a.value }));

  return (
    <div className="p-8 md:p-12" data-testid="wealth-overview">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="wealth-title">
              Portfolio
            </h1>
            <p className="text-slate-600 mt-1 font-ibm">Your household net worth and allocation</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefreshPrices}
              disabled={refreshing}
              className="rounded-sm border-slate-300"
              data-testid="refresh-prices-btn"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh prices'}
            </Button>
            <Button
              onClick={handleGetInsights}
              disabled={loadingInsights}
              className="bg-slate-900 text-white rounded-sm hover:bg-slate-800"
              data-testid="get-insights-btn"
            >
              <Sparkles size={14} className="mr-2" />
              {loadingInsights ? 'Thinking…' : 'AI insights'}
            </Button>
          </div>
        </div>

        {/* NET WORTH HERO */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="md:col-span-2 bg-slate-900 text-white p-8 rounded-sm" data-testid="net-worth-card">
            <p className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-400">Net worth</p>
            <p className="text-5xl font-chivo font-bold mt-2 tabular-nums" data-testid="net-worth-value">
              {fmt(summary?.net_worth, summary?.currency)}
            </p>
            <div className="flex gap-6 mt-6 text-sm font-ibm flex-wrap">
              <div>
                <span className="text-slate-400 uppercase tracking-widest text-xs">Accounts</span>
                <p className="text-xl font-chivo mt-1" data-testid="accounts-count">{summary?.accounts_count ?? 0}</p>
              </div>
              <div>
                <span className="text-slate-400 uppercase tracking-widest text-xs">Holdings</span>
                <p className="text-xl font-chivo mt-1" data-testid="holdings-count">{summary?.holdings_count ?? 0}</p>
              </div>
              {savingsRate && (
                <Link to="/spending" className="ml-auto group" data-testid="savings-rate-badge">
                  <span className="text-slate-400 uppercase tracking-widest text-xs">Savings rate</span>
                  <p className="text-xl font-chivo mt-1 flex items-center gap-2">
                    <span className="tabular-nums">{savingsRate.current_pct.toFixed(1)}%</span>
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      savingsRate.current_pct >= savingsRate.target_pct ? 'bg-emerald-400'
                      : savingsRate.current_pct >= savingsRate.target_pct * 0.75 ? 'bg-amber-400'
                      : 'bg-red-400'
                    }`}></span>
                  </p>
                  <p className="text-xs font-ibm text-slate-400 mt-0.5 group-hover:text-slate-200">
                    Target {savingsRate.target_pct.toFixed(0)}% →
                  </p>
                </Link>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-sm flex flex-col justify-between">
            <div>
              <p className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">Quick actions</p>
              <p className="text-sm font-ibm text-slate-500 mt-1">Add what's in your wealth stack.</p>
            </div>
            <div className="flex flex-col gap-2 mt-4">
              <Link to="/wealth/accounts" className="w-full">
                <Button variant="outline" className="w-full rounded-sm border-slate-300" data-testid="add-account-link">
                  <PlusCircle size={14} className="mr-2" />
                  Add account & holdings
                </Button>
              </Link>
              <Link to="/wealth/allocation" className="w-full">
                <Button variant="outline" className="w-full rounded-sm border-slate-300" data-testid="set-targets-link">
                  <PieIcon size={14} className="mr-2" />
                  Set allocation targets
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* AI INSIGHTS BANNER */}
        {insights && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 border-l-4 border-indigo-500 bg-indigo-50 p-5 rounded-sm"
            data-testid="ai-insights-card"
          >
            <div className="flex items-start gap-3">
              <Sparkles size={20} className="text-indigo-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-chivo font-bold text-slate-900 text-sm uppercase tracking-widest mb-2">
                  Insights for you
                </h3>
                <p className="text-sm font-ibm text-slate-800" data-testid="ai-insights-text">{insights.insights}</p>
                {insights.action_items?.length > 0 && (
                  <ul className="mt-3 space-y-1" data-testid="ai-action-items">
                    {insights.action_items.map((a, idx) => (
                      <li key={idx} className="text-sm font-ibm text-slate-800 flex gap-2">
                        <span className="text-indigo-600">→</span>
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* NET-WORTH HISTORY */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-4" data-testid="networth-history-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-xl font-chivo font-bold text-slate-900">Net worth over time</h2>
            <div className="flex gap-1" data-testid="history-range-switcher">
              {['1m', '3m', '6m', '1y', 'all'].map((r) => (
                <button
                  key={r}
                  onClick={() => setHistoryRange(r)}
                  className={`px-3 py-1 text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm transition-colors ${
                    historyRange === r ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`history-range-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          {history.length < 2 ? (
            <div className="py-10 text-center border border-dashed border-slate-200 rounded-sm">
              <p className="text-sm font-ibm text-slate-500" data-testid="history-empty">
                {history.length === 0
                  ? 'No snapshots yet — check back tomorrow.'
                  : 'Just 1 snapshot so far. Come back tomorrow to start seeing the trend.'}
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="nwGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F172A" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0F172A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748B" style={{ fontSize: '11px' }} />
                <YAxis stroke="#64748B" style={{ fontSize: '11px' }} tickFormatter={(v) => fmt(v).replace('.00', '')} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '2px', color: '#F8FAFC' }}
                  formatter={(v) => fmt(v)}
                />
                <Area type="monotone" dataKey="net_worth" stroke="#0F172A" strokeWidth={2} fill="url(#nwGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ALLOCATION + ACCOUNTS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Allocation donut */}
          <div className="bg-white border border-slate-200 p-6 rounded-sm" data-testid="allocation-chart">
            <h2 className="text-xl font-chivo font-bold text-slate-900 mb-4">Asset allocation</h2>
            {pieData.length === 0 ? (
              <p className="text-sm font-ibm text-slate-500 py-10 text-center">
                Add holdings to see your allocation breakdown.
              </p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} innerRadius={55} paddingAngle={2}>
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={ASSET_COLORS[entry.name] || '#64748B'} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '2px', color: '#F8FAFC' }}
                      formatter={(v) => fmt(v)}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px', textTransform: 'capitalize' }} />
                  </PieChart>
                </ResponsiveContainer>
                <table className="w-full mt-3 text-sm font-ibm">
                  <tbody>
                    {summary.allocation
                      .filter((a) => a.value > 0 || a.target_pct > 0)
                      .map((a) => (
                        <tr key={a.asset_class} className="border-t border-slate-100" data-testid={`alloc-row-${a.asset_class}`}>
                          <td className="py-2 capitalize text-slate-800">{a.asset_class.replace('_', ' ')}</td>
                          <td className="py-2 text-right tabular-nums text-slate-900">{fmt(a.value)}</td>
                          <td className="py-2 text-right tabular-nums text-slate-500 w-20">{a.pct.toFixed(1)}%</td>
                          <td className={`py-2 text-right tabular-nums w-20 font-semibold ${a.drift_pct > 2 ? 'text-amber-600' : a.drift_pct < -2 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {a.drift_pct > 0 ? '+' : ''}{a.drift_pct.toFixed(1)}pp
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* Accounts list */}
          <div className="bg-white border border-slate-200 p-6 rounded-sm" data-testid="accounts-list">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-chivo font-bold text-slate-900">Accounts</h2>
              <Link to="/wealth/accounts" className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600 hover:text-slate-900">
                Manage <ArrowUpRight size={12} className="inline" />
              </Link>
            </div>
            {(summary?.accounts || []).length === 0 ? (
              <div className="py-10 text-center">
                <Wallet size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm font-ibm text-slate-500">No accounts yet.</p>
                <Link to="/wealth/accounts" className="inline-block mt-3 text-xs font-ibm font-semibold uppercase tracking-widest text-slate-900 hover:underline">
                  Add your first account →
                </Link>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="accounts-list-items">
                {summary.accounts.map((a) => (
                  <li
                    key={a.id}
                    data-testid={`account-item-${a.id}`}
                    className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                  >
                    <div>
                      <p className="font-ibm font-medium text-slate-900">{a.name}</p>
                      <p className="text-xs text-slate-500 uppercase tracking-widest font-ibm mt-1">
                        {a.type.replace('_', ' ')} · {a.holdings_count} holding{a.holdings_count === 1 ? '' : 's'}
                      </p>
                    </div>
                    <p className="font-chivo font-bold tabular-nums text-slate-900">
                      {fmt(a.total_value, a.currency)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default WealthOverview;
